var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
// global state
const STATE = {
    language: null,
    languages: {},
    translations: {},
    namespacesInUse: new Set(),
    namespacesResolving: {},
    translationResolver: [],
    languageChangeListener: new Set(),
    translationChangeListener: new Set(),
};
// functions
export function changeLanguage(language) {
    return __awaiter(this, void 0, void 0, function* () {
        if (!STATE.languages[language]) {
            return void console.warn(`@opendash/i18n: Can not change language to '${language}'. You first need to register the language.`);
        }
        STATE.language = language;
        try {
            yield Promise.all(Array.from(STATE.namespacesInUse.values()).map((ns) => resolveTranslation(ns)));
        }
        catch (error) {
            console.error(`@opendash/i18n: Error while resolving translations:`);
            console.error(error);
        }
        for (const callback of Array.from(STATE.languageChangeListener)) {
            Promise.resolve()
                .then(() => callback(STATE.language))
                .catch((error) => {
                console.error(`@opendash/i18n: Error in language change listener:`);
                console.error(error);
            });
        }
    });
}
export function onLanguageChange(callback) {
    STATE.languageChangeListener.add(callback);
    return () => {
        STATE.languageChangeListener.delete(callback);
    };
}
export function onTranslationChange(callback) {
    STATE.translationChangeListener.add(callback);
    return () => {
        STATE.translationChangeListener.delete(callback);
    };
}
export function registerLanguage(key, label, parent = null, isDefault = false) {
    if (key === parent) {
        throw new Error(`@opendash/i18n: registerLanguage Error key === parent is forbidden`);
    }
    STATE.languages[key] = { key, label, parent };
    STATE.translations[key] = {};
}
export function registerTranslationResolver(language, namespace, resolver) {
    STATE.translationResolver.push({ language, namespace, resolver });
}
function runTranslationResolver(namespace, language) {
    return __awaiter(this, void 0, void 0, function* () {
        const resolver = STATE.translationResolver.find((r) => r.language === language && r.namespace === namespace);
        if (!resolver) {
            console.warn(`@opendash/i18n: No translation resolver found for language='${language}' and namespace='${namespace}'`);
            return {};
        }
        try {
            const translationResolved = yield resolver.resolver();
            return flattenTranslation("default" in translationResolved &&
                "__esModule" in translationResolved &&
                translationResolved.__esModule === true
                ? translationResolved.default
                : translationResolved);
        }
        catch (error) {
            console.error(`@opendash/i18n: Error in translation resolver found for language='${language}' and namespace='${namespace}'`, error);
            return {};
        }
    });
}
function resolveTranslation(namespace, language = STATE.language) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        if (!(language in STATE.languages)) {
            return;
        }
        const parentLanguage = (_a = STATE.languages[language]) === null || _a === void 0 ? void 0 : _a.parent;
        if (!namespace || STATE.translations[language][namespace]) {
            return void 0;
        }
        if (STATE.namespacesResolving[language + "~" + namespace]) {
            return new Promise((resolve) => {
                const cancel = onTranslationChange((ns, lng) => {
                    if (ns === namespace && lng === language) {
                        resolve();
                        cancel();
                    }
                });
            });
        }
        STATE.namespacesInUse.add(namespace);
        STATE.namespacesResolving[language + "~" + namespace] = true;
        if (parentLanguage) {
            yield resolveTranslation(namespace, parentLanguage);
        }
        const translation = yield runTranslationResolver(namespace, language);
        if (parentLanguage) {
            STATE.translations[language][namespace] = Object.assign({}, STATE.translations[parentLanguage][namespace], translation);
        }
        else {
            STATE.translations[language][namespace] = translation;
        }
        // console.log(
        //   `@opendash/i18n: resolved`,
        //   language,
        //   namespace,
        //   STATE.translations[language][namespace]
        // );
        STATE.namespacesResolving[language + "~" + namespace] = false;
        for (const callback of Array.from(STATE.translationChangeListener)) {
            Promise.resolve()
                .then(() => callback(namespace, language))
                .catch((error) => {
                console.error(`@opendash/i18n: Error in namespace change listener:`);
                console.error(error);
            });
        }
    });
}
function flattenTranslation(pack, prefix = "", result = {}, separator = ".") {
    for (const key of Object.keys(pack)) {
        if (Array.isArray(pack[key])) {
            console.warn("@opendash/i18n: Arrays are not allowed in Translations", pack[key]);
        }
        else if (typeof pack[key] === "object" && pack[key] !== null) {
            flattenTranslation(pack[key], prefix ? prefix + separator + key : key, result);
        }
        else {
            result[prefix ? prefix + separator + key : key] = pack[key];
        }
    }
    return result;
}
function parseNamespaceAndKey(input) {
    const i = Array.isArray(input) ? input : [input];
    return i.filter(Boolean).map((x) => {
        if (x.includes(" ")) {
            return [null, x];
        }
        const split = x.split(":");
        if (split.length === 1) {
            // console.warn(`@opendash/i18n: Namespace missing in '${x}'`);
            return [null, x];
        }
        if (split.length > 2) {
            console.warn(`@opendash/i18n: Don't use : in your translation keys in '${x}'.`);
        }
        const [namespace, key] = split;
        return [namespace, key];
    });
}
export function translate(input, contextOrDefault) {
    return __awaiter(this, void 0, void 0, function* () {
        const ids = parseNamespaceAndKey(input);
        if (ids.length === 0) {
            return null;
        }
        yield Promise.all(ids.map(([ns]) => resolveTranslation(ns)));
        return translateSync(input, contextOrDefault);
    });
}
export function translateSync(input, contextOrDefault) {
    const ids = parseNamespaceAndKey(input);
    if (ids.length === 0) {
        return null;
    }
    const context = contextOrDefault === (contextOrDefault || "").toString()
        ? { default: contextOrDefault }
        : contextOrDefault || {};
    let result = null;
    for (const [namespace, key] of ids) {
        if (!result && !namespace) {
            result = key;
        }
        if (namespace && !STATE.translations[STATE.language][namespace]) {
            // console.warn(`@opendash/i18n: Namespace not loaded yet '${namespace}'`);
            continue;
        }
        if (!result &&
            "count" in context === true &&
            "context" in context === true) {
            result =
                STATE.translations[STATE.language][namespace][key + "_" + context.context + "_" + (context === null || context === void 0 ? void 0 : context.count)];
        }
        if (!result &&
            "count" in context === true &&
            "context" in context === true) {
            result =
                STATE.translations[STATE.language][namespace][key + "_" + context.context + "_" + (context === null || context === void 0 ? void 0 : context.count)];
        }
        if (!result &&
            "count" in context === true &&
            "context" in context === true &&
            context.count !== 1) {
            result =
                STATE.translations[STATE.language][namespace][key + "_" + context.context + "_plural"];
        }
        if (!result && "context" in context === true) {
            result =
                STATE.translations[STATE.language][namespace][key + "_" + context.context];
        }
        if (!result && "count" in context === true) {
            result =
                STATE.translations[STATE.language][namespace][key + "_" + (context === null || context === void 0 ? void 0 : context.count)];
        }
        if (!result && "count" in context === true && context.count !== 1) {
            result = STATE.translations[STATE.language][namespace][key + "_plural"];
        }
        if (!result) {
            result = STATE.translations[STATE.language][namespace][key];
        }
        if (result) {
            break;
        }
    }
    // console.log(input, result, STATE);
    if (result || result === "") {
        for (const [key, value] of Object.entries(context)) {
            result = result.replace(`{{${key}}}`, value);
        }
    }
    else if ("default" in context) {
        result = context.default;
    }
    else {
        result = ids[0][1];
    }
    return result;
}
export function useTranslation() {
    const [state, setState] = React.useState(0);
    const nsInUseRef = React.useRef(new Set());
    React.useEffect(() => {
        return onTranslationChange((namespace) => {
            // if (nsInUseRef.current.has(namespace)) {
            // trigger a render by setting a state
            setState((x) => x + 1);
            // }
        });
    }, []);
    React.useEffect(() => {
        return onLanguageChange((lang) => {
            // trigger a render by setting a state
            setState((x) => x + 1);
        });
    }, []);
    return function translateSyncReact(input, contextOrDefault) {
        const ids = parseNamespaceAndKey(input);
        for (const [namespace] of ids) {
            if (namespace && !nsInUseRef.current.has(namespace)) {
                nsInUseRef.current.add(namespace);
                resolveTranslation(namespace);
            }
        }
        return translateSync(input, contextOrDefault);
    };
}
export function getCurrentLanguageSync() {
    return STATE.language;
}
export function useCurrentLanguage() {
    const [state, setState] = React.useState(getCurrentLanguageSync());
    React.useEffect(() => {
        return onLanguageChange((lang) => {
            setState(lang);
        });
    });
    return state;
}
export function __debug() {
    for (const resolver of STATE.translationResolver) {
        resolveTranslation(resolver.namespace, resolver.language);
    }
    return STATE;
}
//# sourceMappingURL=index.js.map